import React, { useState, useEffect, useMemo } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import ButtonGroup, { type ButtonGroupOption } from "./ui/button-group";
import DatePicker from "./ui/date-picker";
import {
  getCustomPeriod,
  getOverflowPeriods,
  getPeriodTier,
  getPrimaryPeriods,
  isOverflowPeriodId,
  type Period,
  type PeriodGroup,
} from "@/components/playground/periodConfig";
import { DropDown } from "./ui/dropdown";
import type { MenuItem } from "./ui/button/Button";

export type { Period, PeriodGroup, PeriodTier } from "@/components/playground/periodConfig";

interface TimePeriodSelectorProps {
  period?: string;
  setPeriod: (period: string) => void;
  periods: Period[];
  className?: string;
  actionButtons?: React.ReactNode;
  customDateRange?: [string, string];
  setCustomDateRange?: (range: [string, string]) => void;
  showDatePickerPresets?: boolean;
}

const GROUP_LABELS: Record<PeriodGroup, string> = {
  trailing: "Trailing",
  inception: "Since inception",
};

const chipBaseClasses =
  "cursor-pointer relative inline-flex items-center justify-center leading-none rounded-lg px-3 py-2 text-sm font-medium focus:z-10 transition-colors";

const TimePeriodSelector: React.FC<TimePeriodSelectorProps> = ({
  period: externalPeriod,
  setPeriod,
  periods,
  className = "",
  actionButtons,
  customDateRange = ["", ""],
  setCustomDateRange = () => {},
  showDatePickerPresets = true,
}) => {
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [buttonOptions, setButtonOptions] = useState<ButtonGroupOption<string>[]>([]);

  const primaryPeriods = useMemo(() => getPrimaryPeriods(periods), [periods]);
  const overflowPeriods = useMemo(() => getOverflowPeriods(periods), [periods]);
  const customPeriod = useMemo(() => getCustomPeriod(periods), [periods]);
  const usesTieredLayout = overflowPeriods.length > 0;

  const formatDisplayDate = (dateStr: string) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-");
    return `${day}/${month}/${year}`;
  };

  const getCustomButtonLabel = () => {
    if (
      externalPeriod === "custom" &&
      customDateRange[0] &&
      customDateRange[1]
    ) {
      return `${formatDisplayDate(customDateRange[0])}–${formatDisplayDate(
        customDateRange[1],
      )}`;
    }
    return customPeriod?.label ?? "Custom";
  };

  useEffect(() => {
    if (usesTieredLayout) return;

    const options = periods.map((p) => ({
      value: p.id,
      label: getPeriodTier(p) === "custom" ? getCustomButtonLabel() : p.label,
    }));
    setButtonOptions(options);
  }, [periods, externalPeriod, customDateRange, usesTieredLayout]);

  const period =
    externalPeriod ??
    (periods.some((p) => p.id === "3y") ? "3y" : periods[0]?.id);

  const isOverflowSelected = isOverflowPeriodId(period, periods);
  const isCustomSelected = period === "custom";
  const primaryGroupValue =
    !isOverflowSelected && !isCustomSelected && primaryPeriods.some((p) => p.id === period)
      ? period
      : undefined;

  const selectedOverflowPeriod = overflowPeriods.find((p) => p.id === period);
  const overflowButtonLabel = selectedOverflowPeriod?.label ?? "More";

  const handlePeriodChange = (newPeriod: string) => {
    if (newPeriod === "custom") {
      setIsDatePickerOpen(!isDatePickerOpen);
      if (!isDatePickerOpen || (customDateRange[0] && customDateRange[1])) {
        setPeriod(newPeriod);
      }
      return;
    }

    setIsDatePickerOpen(false);
    setPeriod(newPeriod);
  };

  const handleDateRangeChange = (dates: string | string[]) => {
    if (Array.isArray(dates) && dates.length === 2 && dates[0] && dates[1]) {
      setPeriod("custom");
      setCustomDateRange([dates[0], dates[1]]);
      setIsDatePickerOpen(false);
    }
  };

  const overflowMenuItems = useMemo((): MenuItem[] => {
    const groups: PeriodGroup[] = ["trailing", "inception"];
    const items: MenuItem[] = [];

    groups.forEach((group) => {
      const groupPeriods = overflowPeriods.filter((p) => p.group === group);
      if (!groupPeriods.length) return;

      if (items.length > 0) {
        items.push({ separator: true });
      }
      items.push({ header: GROUP_LABELS[group] });
      groupPeriods.forEach((p) => {
        items.push({
          name: p.label,
          onClick: () => {
            setIsDatePickerOpen(false);
            setPeriod(p.id);
          },
        });
      });
    });

    return items;
  }, [overflowPeriods, setPeriod]);

  const renderChipButton = (
    label: string,
    isSelected: boolean,
    onClick: () => void,
    key?: string,
  ) => (
    <button
      key={key}
      type="button"
      onClick={onClick}
      className={clsx(
        chipBaseClasses,
        "shrink-0 whitespace-nowrap",
        isSelected
          ? "bg-indigo-400 text-white hover:bg-indigo-600 dark:bg-indigo-500 dark:hover:bg-indigo-500 z-10"
          : "bg-white text-gray-900 dark:bg-slate-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700/30",
      )}
    >
      {label}
    </button>
  );

  const renderOverflowMenu = () => (
    <DropDown
      className="inline-flex shrink-0 items-center"
      aspectRatio=""
      label={
        <span className="inline-flex items-center">
          <button
            type="button"
            className={clsx(
              chipBaseClasses,
              "shrink-0 whitespace-nowrap",
              isOverflowSelected
                ? "bg-indigo-400 text-white hover:bg-indigo-600 dark:bg-indigo-500 dark:hover:bg-indigo-500 z-10"
                : "bg-white text-gray-900 dark:bg-slate-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700/30",
            )}
          >
            <span className="inline-flex items-center gap-1">
              {overflowButtonLabel}
              <FontAwesomeIcon icon={faChevronDown} className="h-3 w-3 opacity-70" />
            </span>
          </button>
        </span>
      }
      width="w-52"
    >
      <div className="py-1 px-1">
        {overflowMenuItems.map((item, index) => {
          if (item.separator) {
            return (
              <div
                key={`separator-${index}`}
                className="my-1 h-px bg-slate-200 dark:bg-slate-700"
              />
            );
          }
          if (item.header) {
            return (
              <div
                key={item.header}
                className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400"
              >
                {item.header}
              </div>
            );
          }
          return (
            <button
              key={item.name}
              type="button"
              onClick={item.onClick}
              className="w-full rounded-md px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              {item.name}
            </button>
          );
        })}
      </div>
    </DropDown>
  );

  const renderTieredSelector = () => (
    <div className="inline-flex max-w-full flex-wrap items-center gap-1 rounded-xl p-1 shadow-xs dark:bg-slate-800">
      {primaryPeriods.map((p) =>
        renderChipButton(
          p.label,
          primaryGroupValue === p.id,
          () => handlePeriodChange(p.id),
          p.id,
        ),
      )}

      {overflowPeriods.length > 0 && renderOverflowMenu()}

      {customPeriod &&
        renderChipButton(
          getCustomButtonLabel(),
          isCustomSelected,
          () => handlePeriodChange("custom"),
          "custom",
        )}
    </div>
  );

  const renderLegacySelector = () => (
    <ButtonGroup
      options={buttonOptions}
      value={period}
      onChange={handlePeriodChange}
      fullWidth={false}
      className="flex-wrap"
    />
  );

  return (
    <div
      className={`flex flex-wrap items-center justify-between w-full gap-y-2 ${className}`}
    >
      <div className="flex flex-wrap items-center min-w-0">
        {usesTieredLayout ? renderTieredSelector() : renderLegacySelector()}

        <DatePicker
          mode="range"
          value={customDateRange}
          onChange={handleDateRangeChange}
          isOpen={isDatePickerOpen}
          onOpenChange={setIsDatePickerOpen}
          showPresets={showDatePickerPresets}
          disableFutureDates={true}
          disableCalendar={false}
          trigger={<div className="hidden" />}
        />
      </div>

      {actionButtons && (
        <div className="flex items-center gap-1 ml-auto">{actionButtons}</div>
      )}
    </div>
  );
};

export default TimePeriodSelector;
